import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Stack,
  type SvgIconProps,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  type DataGridProps,
  type GridColDef,
  type GridComparatorFn,
  type GridFilterOperator,
  type GridRenderCellParams,
  type GridSortCellParams,
  type GridSortModel,
  type GridValidRowModel,
} from "@mui/x-data-grid";
import type { UnitType } from "adl-gen/ferovinum/app/db";
import { AnimatedSearchInput } from "components/widgets/inputs/search-input/animated-search-input";
import React, { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { assertNever } from "utils/hx/util/types";
import type { KeyByType } from "utils/utility-types";
import { FormField } from "../../types/formik-types";
import { Link } from "../link/link";
import {
  ProductSummary,
  type ProductSummaryProps,
} from "../product-summary/product-summary";

type TypedGridComparatorFn<V> = (
  v1: V,
  v2: V,
  cellParams1: GridSortCellParams,
  cellParams2: GridSortCellParams,
) => number;

type ColumnAlign = "left" | "center" | "right";

interface ColumnDefBase<V> {
  key: string;
  label: string;
  editable: boolean;
  flex?: number;
  sortComparator?: TypedGridComparatorFn<V>;
  filterOperators?: GridFilterOperator[];
  filterable?: boolean;
  sortable?: boolean;
  align?: ColumnAlign;
  renderHeader?: (params: any) => React.ReactNode;
  getValue?: (row: any) => string;
  hideFromCSVDownload?: boolean;
}

type EditableColumnDef<
  RowType extends GridValidRowModel,
  V,
> = ColumnDefBase<V> & {
  width?: number;
  editable: true;
  renderCell?: (
    params: GridRenderCellParams<RowType, V> & { formField: FormField<V> },
  ) => React.ReactNode | string;
  renderHeader?: () => React.ReactNode;
};

type ReadonlyColumnDef<
  RowType extends GridValidRowModel,
  V,
> = ColumnDefBase<V> & {
  width?: number;
  editable: false;
  renderCell?: (
    params: GridRenderCellParams<RowType, V>,
  ) => React.ReactNode | string;
};

type SpecialColumnDef<RowType extends object> = {
  width?: number;
  specialColumnType: "remove";
  header?: string;
  match: (r1: RowType, r2: RowType) => boolean;
};

function isSpecialColumnDef<RowType extends object, V>(
  columnDef: ColumnDef<RowType, V>,
): columnDef is SpecialColumnDef<RowType> {
  return (
    (columnDef as SpecialColumnDef<RowType>).specialColumnType !== undefined
  );
}

export type ColumnDef<RowType extends object, V> =
  | EditableColumnDef<RowType, V>
  | ReadonlyColumnDef<RowType, V>
  | SpecialColumnDef<RowType>;

export interface NumberOfUnitsValue {
  unitType: UnitType;
  numUnits: number;
  maxUnits: number;
}

type ColumnKeyOrGetter<RowType, K extends keyof RowType, T> =
  | { key: K }
  | { name: string; getter: (row: RowType) => T };

type NumberInputParams = { range?: { min?: number; max?: number } };

function hasKey<RowType extends object, V>(
  columnDef: ColumnDef<RowType, V>,
): columnDef is EditableColumnDef<RowType, V> | ReadonlyColumnDef<RowType, V> {
  return (columnDef as ColumnDefBase<V>).key !== undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getRow<RowType>(params: { id: any; api: any }): RowType {
  return params.api.getRow(params.id) as RowType;
}

export class GridTableColumnFactory<RowType extends object> {
  makeTextColumn<K extends KeyByType<RowType, string>>(
    params: {
      header: string;
      formatter?: (value: string, row: RowType) => React.ReactNode;
      wrap?: boolean;
      width?: number;
      flex?: number;
      align?: ColumnAlign;
      hideFromCSVDownload?: boolean;
      filterOperators?: GridFilterOperator[];
      filterable?: boolean;
    } & ColumnKeyOrGetter<RowType, K, string>,
  ): ColumnDef<RowType, string> {
    const renderCellContent = (value: string, row: RowType) => (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent:
            params.align === "center"
              ? "center"
              : params.align === "right"
                ? "flex-end"
                : "flex-start",
          height: "100%",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            whiteSpace: params.wrap ? "normal" : "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: "1.2",
          }}
        >
          {params.formatter ? params.formatter(value, row) : value}
        </div>
      </div>
    );

    if ("key" in params) {
      return {
        key: String(params.key),
        label: params.header,
        editable: false,
        width: params.width,
        flex: params.flex,
        align: params.align,
        hideFromCSVDownload: params.hideFromCSVDownload,
        filterOperators: params.filterOperators,
        filterable: params.filterable,
        renderCell: (cellParams) => {
          const row = getRow<RowType>(cellParams);
          const value = row[params.key as keyof RowType] as string;
          return renderCellContent(value, row);
        },
      };
    } else {
      return {
        key: params.name,
        label: params.header,
        editable: false,
        width: params.width,
        flex: params.flex,
        align: params.align,
        hideFromCSVDownload: params.hideFromCSVDownload,
        getValue: params.getter,
        filterOperators: params.filterOperators,
        filterable: params.filterable,
        renderCell: (cellParams) => {
          const row = getRow<RowType>(cellParams);
          const value = params.getter(row);
          return renderCellContent(value, row);
        },
      };
    }
  }

  makeHyperLinkColumn<K extends KeyByType<RowType, string>>(
    params: {
      header: string;
      hrefGetter: (row: RowType) => string;
      icon?: React.FC<SvgIconProps>;
      newTab?: boolean;
      flex?: number;
      wrap?: boolean;
      hideFromCSVDownload?: boolean;
    } & ColumnKeyOrGetter<RowType, K, string>,
  ): ColumnDef<RowType, string> {
    if ("key" in params) {
      return {
        key: String(params.key),
        label: params.header,
        editable: false,
        hideFromCSVDownload: params.hideFromCSVDownload,
      };
    }
    return {
      key: params.name,
      label: params.header,
      editable: false,
      flex: params.flex,
      hideFromCSVDownload: params.hideFromCSVDownload,
      renderCell: ({ row }) => (
        <Link
          sx={{
            whiteSpace: params.wrap ? "normal" : "nowrap",
            wordBreak: params.wrap ? "break-word" : "normal",
          }}
          href={params.hrefGetter(row)}
          display="flex"
          alignItems="center"
          target={params.newTab ? "_blank" : undefined}
          rel={params.newTab ? "noopener noreferrer" : undefined}
        >
          {params.getter(row)}
          {params.icon &&
            React.createElement(params.icon, {
              fontSize: "small",
              style: { marginLeft: 4 },
            })}
        </Link>
      ),
    };
  }

  makeProductSummaryColumn<K extends KeyByType<RowType, ProductSummaryProps>>(
    params: { header?: string } & ColumnKeyOrGetter<
      RowType,
      K,
      ProductSummaryProps
    >,
  ): ColumnDef<RowType, ProductSummaryProps> {
    if ("key" in params) {
      return {
        key: String(params.key),
        label: params.header ?? "Product",
        editable: false,
        flex: 2,
        renderCell: (cellParams) => {
          const row = getRow<RowType>(cellParams);
          const productSummaryProps = row[params.key] as ProductSummaryProps;
          return (
            <Stack p={1}>
              <ProductSummary {...productSummaryProps} />
            </Stack>
          );
        },
        sortComparator: (v1, v2) => {
          const code1 = v1?.code || "";
          const code2 = v2?.code || "";
          return code1.localeCompare(code2);
        },
        getValue: (row) => {
          const product = row[params.key] as ProductSummaryProps;
          if (!product) return "";
          return `${product.code || ""} - ${product.name || ""} (${product.producerName || ""})`;
        },
      };
    }
    return {
      key: params.name,
      label: params.header ?? "Product",
      editable: false,
      flex: 2,
      renderCell: (cellParams) => {
        const row = getRow<RowType>(cellParams);
        return (
          <Stack p={1}>
            <ProductSummary {...params.getter(row)} />
          </Stack>
        );
      },
      sortComparator: (v1, v2) => {
        const code1 = v1?.code || "";
        const code2 = v2?.code || "";
        return code1.localeCompare(code2);
      },
      getValue: (row) => {
        const product = params.getter(row);
        if (!product) return "";
        return `${product.code || ""} - ${product.name || ""} (${product.producerName || ""})`;
      },
    };
  }

  makeCheckboxColumn<K extends KeyByType<RowType, boolean>>(params: {
    key: K;
    header?: string;
    getter: (row: RowType) => boolean;
    onChange: (row: RowType, checked: boolean) => void;
    onSelectAll?: (checked: boolean) => void;
    hideFromCSVDownload?: boolean;
  }): ColumnDef<RowType, boolean> {
    return {
      key: String(params.key),
      label: params.header ?? "",
      editable: true,
      flex: 0.75,
      hideFromCSVDownload: params.hideFromCSVDownload,
      renderHeader: () => (
        <Checkbox
          onChange={(_e, checked) => {
            params.onSelectAll?.(checked);
          }}
        />
      ),
      renderCell: (cellParams) => {
        const row = getRow<RowType>(cellParams);
        return (
          <Checkbox
            checked={params.getter(row)}
            onChange={(_e, checked) => params.onChange(row, checked)}
          />
        );
      },
    };
  }

  makeRemovalColumn(params: {
    header?: string;
    match: (r1: RowType, r2: RowType) => boolean;
  }): ColumnDef<RowType, boolean> {
    return {
      specialColumnType: "remove",
      header: params.header,
      match: params.match,
    } as SpecialColumnDef<RowType>;
  }

  makeEditableNumberColumn<K extends KeyByType<RowType, number>>(params: {
    key: K;
    header?: string;
    displayFormatter?: (row: RowType) => string;
    editableCheck?: (row: RowType) => boolean;
    endAdornmentTextProvider?: (row: RowType) => string;
    inputParams?: ((row: RowType) => NumberInputParams) | NumberInputParams;
    hideFromCSVDownload?: boolean;
  }): ColumnDef<RowType, number> {
    const {
      inputParams,
      displayFormatter,
      editableCheck,
      endAdornmentTextProvider,
    } = params;
    return {
      key: String(params.key),
      label: params.header ?? "Number of units",
      editable: true,
      hideFromCSVDownload: params.hideFromCSVDownload,
      renderCell: this.makeRenderNumberOfUnitsCell(
        displayFormatter,
        editableCheck,
        endAdornmentTextProvider,
        inputParams,
      ),
    };
  }

  makeNumberColumn<K extends KeyByType<RowType, number>>(params: {
    key: K;
    header?: string;
    displayFormatter?: (row: RowType) => React.ReactNode;
    flex?: number;
    hideFromCSVDownload?: boolean;
  }): ColumnDef<RowType, number> {
    const { displayFormatter } = params;
    return {
      key: String(params.key),
      label: params.header ?? "Number of units",
      editable: false,
      flex: params.flex,
      hideFromCSVDownload: params.hideFromCSVDownload,
      renderCell: (params) => {
        const row = getRow<RowType>(params);
        return displayFormatter?.(row) ?? row[params.field as keyof RowType];
      },
    };
  }

  makeDateColumn<K extends KeyByType<RowType, string | Date>>(params: {
    key: K;
    header?: string;
    displayFormatter?: (row: RowType) => React.ReactNode;
    flex?: number;
    hideFromCSVDownload?: boolean;
  }): ColumnDef<RowType, Date> {
    const { displayFormatter } = params;

    const dateComparator: GridComparatorFn = (v1, v2) => {
      const date1 = new Date(v1 as string | number | Date);
      const date2 = new Date(v2 as string | number | Date);

      if (Number.isNaN(date1.getTime()) || Number.isNaN(date2.getTime()))
        return 0;

      return date1.getTime() - date2.getTime();
    };

    return {
      key: String(params.key),
      label: params.header ?? "Date",
      editable: false,
      flex: params.flex,
      hideFromCSVDownload: params.hideFromCSVDownload,
      renderCell: (params) => {
        const row = getRow<RowType>(params);
        const fieldValue = row[params.field as keyof RowType];
        return displayFormatter ? displayFormatter(row) : fieldValue;
      },
      sortComparator: dateComparator,
    };
  }

  makeProgressStepperColumn<
    K extends KeyByType<RowType, React.ReactNode>,
  >(params: {
    key: K;
    header?: string;
    flex?: number;
    align?: ColumnAlign;
    sortComparator?: GridComparatorFn;
    hideFromCSVDownload?: boolean;
  }): ColumnDef<RowType, React.ReactNode> {
    return {
      key: String(params.key),
      label: params.header ?? "Widget",
      editable: false,
      align: params.align ?? "left",
      flex: params.flex ?? 1,
      hideFromCSVDownload: params.hideFromCSVDownload,
      renderCell: ({ row }) => (
        <Box
          sx={{
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row[params.key]}
        </Box>
      ),
      sortComparator: params.sortComparator,
    };
  }

  makeToggleColumn<K extends KeyByType<RowType, boolean>>(params: {
    key: K;
    header?: string;
    getter: (row: RowType) => boolean;
    onChange: (row: RowType, checked: boolean) => void;
    hideFromCSVDownload?: boolean;
  }): ColumnDef<RowType, boolean> {
    return {
      key: String(params.key),
      label: params.header ?? "",
      editable: true,
      flex: 0.25,
      hideFromCSVDownload: params.hideFromCSVDownload,
      renderCell: (cellParams) => {
        const row = getRow<RowType>(cellParams);
        return (
          <Switch
            checked={params.getter(row)}
            onChange={(_e, checked) => params.onChange(row, checked)}
          />
        );
      },
    };
  }

  makeButtonColumn<K extends KeyByType<RowType, string>>(
    params: {
      header: string;
      buttonElement: (row: RowType) => React.ReactNode;
      getter: (row: RowType) => string;
      onClick: (row: RowType) => void;
      hideFromCSVDownload?: boolean;
    } & ColumnKeyOrGetter<RowType, K, string>,
  ): ColumnDef<RowType, string> {
    const { header, buttonElement, onClick } = params;

    return {
      key: "key" in params ? String(params.key) : params.name,
      label: header,
      editable: false,
      hideFromCSVDownload: params.hideFromCSVDownload,
      renderCell: (cellParams) => {
        const row = getRow<RowType>(cellParams);
        return (
          <div onClick={() => onClick(row)} style={{ cursor: "pointer" }}>
            {buttonElement(row)}
          </div>
        );
      },
    };
  }

  makeNodeColumn<K extends keyof RowType>(params: {
    key: K | string;
    header?: string;
    renderNode: (row: RowType) => React.ReactNode;
    getValue?: (row: RowType) => string;
    hideFromCSVDownload?: boolean;
    flex?: number;
    align?: ColumnAlign;
    width?: number;
    filterable?: boolean;
    filterOperators?: GridFilterOperator[];
  }): ColumnDef<RowType, React.ReactNode> {
    const {
      key,
      header,
      align,
      renderNode,
      flex,
      getValue,
      hideFromCSVDownload,
    } = params;

    return {
      key: String(key),
      label: header ?? "",
      editable: false,
      flex: flex ?? 1,
      align,
      width: params.width,
      hideFromCSVDownload,
      filterable: params.filterable,
      renderCell: (cellParams) => {
        const row = getRow<RowType>(cellParams);
        return renderNode(row);
      },
      getValue,
    };
  }

  makeBooleanColumn<K extends KeyByType<RowType, boolean | null>>(params: {
    key: K;
    header?: string;
    trueIcon?: React.FC<SvgIconProps>;
    falseIcon?: React.FC<SvgIconProps>;
    nullIcon?: React.FC<SvgIconProps>;
    hideFromCSVDownload?: boolean;
  }): ColumnDef<RowType, boolean | null> {
    return {
      key: String(params.key),
      label: params.header ?? "Boolean",
      editable: false,
      hideFromCSVDownload: params.hideFromCSVDownload,
      renderCell: ({ row }) => {
        const value = row[params.key as keyof RowType];
        let IconComponent: React.FC<SvgIconProps> | undefined;
        let color: string;

        if (value === true) {
          IconComponent = params.trueIcon;
          color = "success.main";
        } else if (value === false) {
          IconComponent = params.falseIcon;
          color = "error.main";
        } else {
          IconComponent = params.nullIcon;
          color = "action.disabled";
        }

        return (
          <IconButton
            size="small"
            sx={{
              color: color,
            }}
          >
            {IconComponent && <IconComponent />}
          </IconButton>
        );
      },
      sortComparator: (v1: boolean | null, v2: boolean | null) => {
        if (v1 === v2) return 0;
        if (v1 === null) return 1;
        if (v2 === null) return -1;
        return v1 ? -1 : 1;
      },
    };
  }

  private makeRenderNumberOfUnitsCell(
    displayFormatter?: (row: RowType) => string,
    editableCheck?: (row: RowType) => boolean,
    endAdornmentTextProvider?: (row: RowType) => string,
    inputParams?: ((row: RowType) => NumberInputParams) | NumberInputParams,
  ): EditableColumnDef<RowType, number>["renderCell"] {
    const renderer: EditableColumnDef<RowType, number>["renderCell"] = (
      params,
    ) => {
      const row = getRow<RowType>(params);
      const value = params.value;
      if (!editableCheck?.(row)) {
        return displayFormatter?.(row);
      }

      const inputParamsValues: NumberInputParams | undefined =
        typeof inputParams === "function" ? inputParams(row) : inputParams;

      return (
        <TextField
          value={value}
          onFocus={() => {
            if (params.api.getCellMode(params.id, params.field) === "edit") {
              params.api.startCellEditMode({
                id: params.id,
                field: params.field,
              });
            }
          }}
          onBlur={() => {
            if (params.api.getCellMode(params.id, params.field) === "edit") {
              params.api.stopCellEditMode({
                id: params.id,
                field: params.field,
              });
            }
          }}
          name={params.field}
          onChange={(e) => {
            const newValue = Number(e.target.value);
            if (inputParamsValues?.range) {
              if (
                inputParamsValues.range.min &&
                newValue < inputParamsValues.range.min
              ) {
                return;
              }
              if (
                inputParamsValues.range.max &&
                newValue > inputParamsValues.range.max
              ) {
                return;
              }
            }
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: newValue,
            });
            if (params.formField) {
              params.formField.set(newValue);
            }
          }}
          type="number"
          InputProps={{
            inputProps: { ...inputParamsValues?.range },
            endAdornment: (
              <InputAdornment position="end">
                {endAdornmentTextProvider?.(row)}
              </InputAdornment>
            ),
          }}
          error={params.formField.hasError(true)}
          sx={{ paddingY: 1 }}
        />
      );
    };
    return renderer;
  }
}

const makeHeightProps = (tableHeight?: number | "auto") => ({
  grid: tableHeight === "auto" ? { autoHeight: true } : {},
  container:
    tableHeight === "auto" ? {} : { minHeight: 300, height: tableHeight },
});

/**
 * Given a search term and an object, recursively search the object for the search term.
 */
function containsSearchTerm(obj: unknown, searchTerm: string): boolean {
  const searchRecursive = (value: unknown, depth = 0): boolean => {
    // this is to prevent infinite recursion
    if (depth > 5) return false;

    if (React.isValidElement(value)) return false;

    if (typeof value === "object" && value !== null) {
      return Object.values(value).some((v) => searchRecursive(v, depth + 1));
    }

    return String(value).toLowerCase().includes(searchTerm);
  };

  return searchRecursive(obj);
}

// NB this is a helper function to safely get displayable value
function getDisplayableValue(value: any): string {
  if (value === null || value === undefined) {
    return "";
  }

  // specially if its a react element or has a circular reference, try to get textContent
  if (React.isValidElement(value)) {
    const div = document.createElement("div");
    ReactDOM.render(value, div);
    const text = div.textContent || "";
    ReactDOM.unmountComponentAtNode(div);
    return text;
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  // extract text from react elements
  if (React.isValidElement(value)) {
    const getTextFromChildren = (children: any): string => {
      if (!children) return "";

      if (
        typeof children === "string" ||
        typeof children === "number" ||
        typeof children === "boolean"
      ) {
        return String(children);
      }

      if (Array.isArray(children)) {
        return children.map(getTextFromChildren).join(" ");
      }

      if (React.isValidElement(children)) {
        const reactElement = children as React.ReactElement;
        if (reactElement.props.children !== undefined) {
          return getTextFromChildren(reactElement.props.children);
        }
        if (reactElement.props.value !== undefined) {
          return String(reactElement.props.value);
        }
        if (reactElement.props.text !== undefined) {
          return String(reactElement.props.text);
        }
      }

      return "";
    };

    return getTextFromChildren(value).trim();
  }

  // if its an obj that contain displayable data
  if (typeof value === "object") {
    // try toString first if it's been overridden
    if (value.toString && value.toString !== Object.prototype.toString) {
      const stringValue = value.toString();
      if (stringValue !== "[object Object]") {
        return stringValue;
      }
    }

    const textProperties = ["name", "title", "label", "text", "value", "code"];
    for (const prop of textProperties) {
      if (prop in value && value[prop] !== undefined) {
        return String(value[prop]);
      }
    }

    try {
      const simplifiedObject = Object.fromEntries(
        Object.entries(value).filter(([_, v]) => {
          return typeof v !== "function" && !React.isValidElement(v);
        }),
      );
      return Object.values(simplifiedObject)
        .filter((v) => v !== undefined && v !== null)
        .join(" ");
    } catch {
      return "";
    }
  }

  return "";
}

function downloadCSV<
  InputRowType extends object,
  SupportRowType extends object,
>(
  rows: InputRowType[],
  columns: ColumnDef<InputRowType & SupportRowType, any>[],
): void {
  // First filter with hasKey to infer type correctly
  const regularColumns = columns.filter(hasKey);

  // Then filter for non-hidden columns
  const visibleColumns = regularColumns.filter(
    (col) => !col.hideFromCSVDownload,
  );

  // Then map map to get headers and rows
  const headers = visibleColumns.map((col) => col.label || col.key).join(",");
  const csvRows = rows.map((row) => {
    return visibleColumns
      .map((col) => {
        const value = col.getValue
          ? col.getValue(row)
          : row[col.key as keyof InputRowType];
        const displayValue = getDisplayableValue(value);
        return `"${displayValue.replace(/"/g, '""')}"`;
      })
      .join(",");
  });

  const csvContent = [headers, ...csvRows].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", "table-export.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

interface BaseRowType {
  id?: string | number;
}

export function GridTable<
  InputRowType extends BaseRowType,
  SupportRowType extends object = object,
>(
  props: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: ColumnDef<InputRowType & SupportRowType, any>[];
    inputRowsField: FormField<InputRowType[]> | InputRowType[];
    tableHeight?: number | "auto";
    sortModel?: GridSortModel;
    onSortModelChange?: (sortModel: GridSortModel) => void;
    disabled?: boolean;
    searchField?: boolean;
    csvDownload?: boolean;
    rowHeight?: number;
    filters?: React.FC[]; // Array of React Filter Components (e.g., CurrencySelect, other filters)
    CustomDownload?: React.ReactElement;
    getRowId?: (row: InputRowType) => string | number;
  } & Omit<Partial<DataGridProps>, "columns">,
) {
  const {
    columns,
    inputRowsField,
    tableHeight,
    sortModel,
    onSortModelChange,
    sx,
    searchField,
    csvDownload,
    rowHeight,
    filters = [],
    CustomDownload,
    getRowId,
    ...otherProps
  } = props;
  const displayOnly = !(inputRowsField instanceof FormField);
  const [searchText, setSearchText] = useState("");
  const gridColumns: GridColDef[] = useMemo(() => {
    return columns.map((c) => {
      const colDef = processColumnDef(c, inputRowsField);
      const col: GridColDef = {
        field: String(colDef.key),
        headerName: colDef.label,
        editable: false,
        flex: colDef.flex ?? 1,
        width: colDef.width,
        align: colDef.align,
        renderHeader: colDef.renderHeader,
        filterable: colDef.filterable,
        sortable: colDef.sortable === undefined ? true : colDef.sortable,
      };
      if (!colDef.editable) {
        const renderCell = colDef.renderCell;
        col.renderCell = renderCell;
      } else if (!displayOnly) {
        const renderCell = colDef.renderCell;
        col.renderCell =
          renderCell &&
          ((params) => {
            const row = getRow<InputRowType & { id: string | number }>(params);

            if (
              getRowId &&
              !displayOnly &&
              inputRowsField instanceof FormField
            ) {
              const allRows = inputRowsField.get() || [];
              const customId = getRowId(row as InputRowType);

              const rowIndex = allRows.findIndex(
                (r) => getRowId(r) === customId,
              );

              if (rowIndex !== -1) {
                const formField = inputRowsField.getSubField([
                  rowIndex,
                  colDef.key,
                ]);
                return renderCell({ ...params, formField });
              }
            }

            const formField = inputRowsField.getSubField([row.id, colDef.key]);
            return renderCell({ ...params, formField });
          });
      } else {
        throw new Error(
          "Editable columns are not supported in display only mode",
        );
      }
      if (colDef.sortComparator) {
        col.sortComparator = colDef.sortComparator as GridComparatorFn;
      }
      if (colDef.filterOperators) {
        col.filterOperators = colDef.filterOperators;
      }
      return col;
    });
  }, [columns, displayOnly, inputRowsField, getRowId]);

  const rows = useMemo(() => {
    const allRows = displayOnly ? inputRowsField : (inputRowsField.get() ?? []);
    if (!searchText || allRows.length === 0) {
      return allRows;
    }
    const searchTextLower = searchText.toLowerCase();
    const searchableColumns = columns.filter((col) => hasKey(col));
    return allRows.filter((row) => {
      return searchableColumns.some((col) => {
        const value = row[col.key as keyof typeof row];
        if (value) {
          if (typeof value === "object") {
            return containsSearchTerm(value, searchTextLower);
          }
          return String(value).toLowerCase().includes(searchTextLower);
        }
        if (col.getValue) {
          const value = col.getValue(row);
          return String(value).toLowerCase().includes(searchTextLower);
        }
      });
    });
  }, [displayOnly, inputRowsField, searchText, columns]);

  const rowsWithIds = rows.map((row: InputRowType, idx) => ({
    ...row,
    id: (row as any).id ?? idx,
  }));

  const errorValue = Array.isArray(inputRowsField)
    ? undefined
    : inputRowsField.getError(true);
  // error is a string means it's related to the overal union field
  const tableError = typeof errorValue === "string" ? errorValue : undefined;

  const heightProps = useMemo(
    () => makeHeightProps(tableHeight),
    [tableHeight],
  );
  return (
    <Stack {...heightProps.container} flexGrow={1}>
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        {(searchField || csvDownload || filters) && (
          <Box mb={2} display="flex" gap={2} flexGrow={1}>
            {filters.length > 0 && (
              <Stack direction="row" spacing={2} alignItems="center">
                {filters.map((FilterComponent, index) => (
                  <FilterComponent key={index} /> // Render each filter component
                ))}
              </Stack>
            )}
            {searchField && (
              <AnimatedSearchInput
                onChange={setSearchText}
                searchText={searchText}
              />
            )}
            {csvDownload &&
              (CustomDownload ?? (
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={() =>
                    downloadCSV<InputRowType, SupportRowType>(rows, columns)
                  }
                  sx={{
                    width: 55,
                    height: 55,
                    minHeight: 55,
                    minWidth: 55,
                    borderRadius: "50%",
                    padding: 0,
                    "& .MuiButton-startIcon": {
                      margin: 0,
                    },
                  }}
                />
              ))}
          </Box>
        )}
      </Stack>

      <DataGrid
        rows={rowsWithIds}
        columns={gridColumns}
        getRowHeight={() => null}
        rowHeight={rowHeight ?? 100}
        isRowSelectable={(params) => params.row.isSelectable}
        checkboxSelection={false}
        disableRowSelectionOnClick
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        getRowId={getRowId ? (row) => getRowId(row as InputRowType) : undefined}
        {...heightProps.grid}
        {...otherProps}
        sx={{
          backgroundColor: "white",
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid #E2E8F0",
          },
          // Note (Nicole): Currently this is the only way to disable the cell selection outline
          // https://github.com/mui/mui-x/issues/2429#issuecomment-905492502
          "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          // Note (MB): I've disabled the pointer cursor on tables for now, as it's not clear what it was meant for
          // "& .MuiDataGrid-row": {
          //   cursor: "pointer",
          // },
          "& .monthlyFee-edit-input.MuiDataGrid-cell--editing": {
            padding: 0,
            maxWidth: "unset !important",
            outline: "none !important",
            backgroundColor: "common.lightYellow",
          },
          ".MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
          ...sx,
        }}
      />
      <Typography color="error">{tableError}</Typography>
    </Stack>
  );
}

function processColumnDef<
  RowType extends object,
  ExpandedRowType extends RowType,
  V,
>(
  columnDef: ColumnDef<ExpandedRowType, V>,
  formField: FormField<RowType[]> | RowType[],
):
  | EditableColumnDef<ExpandedRowType, V>
  | ReadonlyColumnDef<ExpandedRowType, V> {
  if (isSpecialColumnDef(columnDef)) {
    switch (columnDef.specialColumnType) {
      case "remove": {
        return makeRemoveColumnDef(columnDef.header, (row) => {
          if (Array.isArray(formField)) {
            formField.splice(formField.indexOf(row), 1);
          } else {
            const rows = formField
              .get()
              ?.filter((r) => !columnDef.match(r as ExpandedRowType, row));
            formField.set(rows);
          }
        });
      }
      default:
        assertNever(columnDef.specialColumnType);
    }
  }
  return columnDef;
}

function makeRemoveColumnDef<RowType extends object, V>(
  label: string | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRemove: (row: any) => void,
): ReadonlyColumnDef<RowType, V> {
  return {
    key: "remove",
    label: label ?? "",
    editable: false,
    flex: 0.25,
    renderCell: ({ row }) => (
      <IconButton
        onClick={() => onRemove(row)}
        size="small"
        sx={{
          color: "common.grey5",
        }}
      >
        <CloseIcon />
      </IconButton>
    ),
  };
}
